import { FC, useContext } from "react";
import cx from "classnames";
import { MAP_CONTENT_MEMBER_OPTIONS, MAP_CONTENT_PROVIDER_GROUPS } from "@app/projects/network-assessment/multi-map-export/utils/mapContentOptions";
import { SwatchLabel, swatchPathStyle } from "@app/projects/network-assessment/multi-map-export/components/SwatchLabel";
import { getSampleCountyFill, getVisibleBoundaryLayers, getVisibleContentLayers, MapExportSampleContentLayer, ProviderLeafOption, usesBaseMapImage } from "@app/projects/network-assessment/multi-map-export/utils/mapExportSampleSelection";
import { boundingBoxOf, correctedFrameBounds, GeometryBoundingBox, labelPointFor, projectGeometryToPath, projectPointToPercent, SampleBackgroundFit, SampleViewBox, scaleBackgroundFit } from "@app/projects/network-assessment/multi-map-export/utils/mapExportSampleProjection";
import { MAP_EXPORT_SAMPLE_GEOMETRY } from "@app/projects/network-assessment/multi-map-export/data/mapExportSampleGeometry";
import { MAP_EXPORT_SAMPLE_POINTS } from "@app/projects/network-assessment/multi-map-export/data/mapExportSamplePoints";
import { MARKET_PROVIDER_COLOR, ZIP_CODE_BORDER_COLOR } from "@app/projects/network-assessment/maps/network-coverage-map/NetworkCoverageMapsConstants";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { DEFAULT_MAP_STYLE_BASE_MAP, DEFAULT_MAP_STYLE_SHOW_SCALE_BAR, DEFAULT_MAP_STYLE_ZOOM_LEVEL } from "@app/projects/network-assessment/multi-map-export/state-management/multiMapExportStateManagement";
import { MultiMapExportBaseMap, MultiMapExportBoundaryLayer, MultiMapExportMemberLayer, MultiMapExportZoomLevel } from "@app/projects/network-assessment/multi-map-export/interfaces";
import { MultiMapExportContext } from "@app/projects/network-assessment/multi-map-export/MultiMapExportContext";
import { QesIcon } from "@app/shared/icons/QESIcon";

// Only the 3 boundary lines — not labels/ZipCodePoints. Widest-to-narrowest order.
const SAMPLE_BOUNDARY_LAYERS: readonly MultiMapExportBoundaryLayer[] = [
  MultiMapExportBoundaryLayer.StateBoundaries,
  MultiMapExportBoundaryLayer.CountyBoundaries,
  MultiMapExportBoundaryLayer.ZipCodeBoundaries,
];

// Zip renders separately further down (34 polygons, not one shape).
const SAMPLE_SHAPE_BOUNDARY_LAYERS: readonly MultiMapExportBoundaryLayer[] = [
  MultiMapExportBoundaryLayer.StateBoundaries,
  MultiMapExportBoundaryLayer.CountyBoundaries,
];

const SAMPLE_VIEW_BOX: SampleViewBox = { height: 100, width: 100 };
const SAMPLE_FRAME_ASPECT_RATIO = 4 / 3;

// Only the Non-Terminated provider point ever sits past the county line, so only Map Contents zoom with
// that option selected needs the padding; every other case uses the tight county frame.
const SAMPLE_COUNTY_BOUNDS = boundingBoxOf(MAP_EXPORT_SAMPLE_GEOMETRY.county.geometry);
const SAMPLE_TIGHT_BOUNDS = correctedFrameBounds(SAMPLE_COUNTY_BOUNDS, 0, SAMPLE_FRAME_ASPECT_RATIO);
const SAMPLE_PADDED_BOUNDS = correctedFrameBounds(SAMPLE_COUNTY_BOUNDS, 0.45, SAMPLE_FRAME_ASPECT_RATIO);

// Tuned against Light/Terrain for Zoom to Map Contents.
const SAMPLE_MAP_CONTENTS_BACKGROUND_FIT: SampleBackgroundFit = { positionX: 6, positionY: 89, sizeX: 154, sizeY: 154 };

interface SampleZoomLayers {
  readonly backgroundFit: SampleBackgroundFit;
  readonly bounds: GeometryBoundingBox;
  readonly countyPath: string;
  readonly scaleBarMiles: number;
  readonly statePath: string;
  readonly zipPaths: readonly { readonly d: string, readonly zipCode: string }[];
}

const layersFor = (bounds: GeometryBoundingBox, backgroundFit: SampleBackgroundFit, scaleBarMiles: number): SampleZoomLayers => ({
  backgroundFit,
  bounds,
  countyPath: projectGeometryToPath(MAP_EXPORT_SAMPLE_GEOMETRY.county.geometry, SAMPLE_VIEW_BOX, bounds),
  scaleBarMiles,
  statePath: projectGeometryToPath(MAP_EXPORT_SAMPLE_GEOMETRY.state.geometry, SAMPLE_VIEW_BOX, bounds),
  zipPaths: MAP_EXPORT_SAMPLE_GEOMETRY.zipCodes.map((feature) => ({
    d: projectGeometryToPath(feature.geometry, SAMPLE_VIEW_BOX, bounds),
    zipCode: feature.properties?.zipCode ?? "",
  })),
});

// Hardcoded per zoom level (not derived from the bar's fixed pixel width) — Map Contents shows a wider
// area than County in the same bar length, so its mileage is bigger too.
const SAMPLE_PADDED_LAYERS = layersFor(SAMPLE_PADDED_BOUNDS, SAMPLE_MAP_CONTENTS_BACKGROUND_FIT, 10);
const SAMPLE_TIGHT_LAYERS = layersFor(
  SAMPLE_TIGHT_BOUNDS,
  scaleBackgroundFit(SAMPLE_MAP_CONTENTS_BACKGROUND_FIT, SAMPLE_PADDED_BOUNDS, SAMPLE_TIGHT_BOUNDS),
  8
);

// Label anchors as lng/lat, not fixed screen percent, so they track the county as bounds change.
const KANSAS_LABEL_POINT: readonly [number, number] = [-94.8318, 39.063];
const JOHNSON_COUNTY_LABEL_POINT: readonly [number, number] = [-94.8318, 38.8805];

const SAMPLE_ZIP_LABEL_POINTS = MAP_EXPORT_SAMPLE_GEOMETRY.zipCodes.map((feature) => ({
  point: labelPointFor(feature.geometry),
  zipCode: feature.properties?.zipCode ?? "",
}));

// PO boxes with no polygon — label only, no boundary to draw.
const SAMPLE_ZIP_POINT_LABEL_POINTS = MAP_EXPORT_SAMPLE_GEOMETRY.zipCodePoints.map((feature) => {
  const [lng, lat] = feature.geometry.coordinates;

  return { point: [lng, lat] as const, zipCode: feature.properties?.zipCode ?? "" };
});

// Every label anchor is a fixed lng/lat, which can fall outside the frame once the tighter Zoom to County
// bounds are active — skip those instead of letting them spill past the card edge. Applies to state/county
// place labels as well as zip labels: only Johnson County's own label is guaranteed to stay inside.
const isPercentInFrame = (percent: { readonly left: number, readonly top: number }): boolean =>
  percent.left >= 0 && percent.left <= 100 && percent.top >= 0 && percent.top <= 100;

const shapePathFor = (layer: MultiMapExportBoundaryLayer, layers: SampleZoomLayers): string =>
  (layer === MultiMapExportBoundaryLayer.StateBoundaries ? layers.statePath : layers.countyPath);

// Matches the real map's own boundary colors (NetworkCoverageMap's COUNTY_BORDER_LAYER_ID and zip boundary
// layer), not new swatch choices. State boundaries have no layer on the real map, so no entry here — they
// keep the SCSS default.
const BOUNDARY_STROKE_COLOR: Partial<Record<MultiMapExportBoundaryLayer, string>> = {
  [MultiMapExportBoundaryLayer.CountyBoundaries]: MARKET_PROVIDER_COLOR,
  [MultiMapExportBoundaryLayer.ZipCodeBoundaries]: ZIP_CODE_BORDER_COLOR,
};

const renderZipLabel = (
  { point, zipCode }: { readonly point: readonly [number, number], readonly zipCode: string },
  bounds: GeometryBoundingBox
) => {
  const percent = projectPointToPercent(point, bounds);

  return isPercentInFrame(percent) && (
    <span className="multi-map-export__sample-zip-label" key={zipCode} style={{ left: `${percent.left}%`, top: `${percent.top}%` }}>
      {zipCode}
    </span>
  );
};

interface ContentLayerSwatch {
  readonly label: string;
  readonly swatchColor: string;
  readonly swatchIcon: string;
}

// Built once, shared by markers and the legend.
const CONTENT_LAYER_OPTIONS = new Map<MapExportSampleContentLayer, ContentLayerSwatch>([
  ...MAP_CONTENT_MEMBER_OPTIONS.map((option) => [option.value, option] as const),
  ...MAP_CONTENT_PROVIDER_GROUPS.flatMap((group) => group.options).map((option) =>
    [option.value as ProviderLeafOption, option] as const),
]);

const isMemberLayer = (layer: MapExportSampleContentLayer): layer is MultiMapExportMemberLayer =>
  layer === MultiMapExportMemberLayer.WithAccess || layer === MultiMapExportMemberLayer.WithoutAccess;

// The legend has no group header, so leaf labels need one baked in ("Network Providers" instead of
// bare "Non-Terminated"). Only the 6 rows that need a qualifier are overridden here.
const CONTENT_LAYER_LEGEND_LABELS: Partial<Record<ProviderLeafOption, string>> = {
  [ProvidersFilterOptions.NonTerminated]: "Network Providers",
  [ProvidersFilterOptions.Terminated]: "Terminated Network Providers",
  [ProvidersFilterOptions.AvailableMarket]: "Market Providers",
  [ProvidersFilterOptions.TargetedMarket]: "Targeted Market Providers",
  [ProvidersFilterOptions.ExcludedMarket]: "Excluded Market Providers",
  [ProvidersFilterOptions.AvailableProspect]: "Prospect Providers",
  [ProvidersFilterOptions.TargetedProspect]: "Targeted Prospect Providers",
  [ProvidersFilterOptions.ExcludedProspect]: "Excluded Prospect Providers",
};

const legendLabelFor = (layer: MapExportSampleContentLayer, fallback: string): string =>
  (isMemberLayer(layer) ? fallback : CONTENT_LAYER_LEGEND_LABELS[layer] ?? fallback);

export const MapExportSample: FC<React.PropsWithChildren<unknown>> = () => {
  const { selections } = useContext(MultiMapExportContext);
  const visibleBoundaryLayers = new Set(getVisibleBoundaryLayers(selections));
  const activeSampleBoundaryLayers = SAMPLE_BOUNDARY_LAYERS.filter((layer) => visibleBoundaryLayers.has(layer));
  const activeShapeBoundaryLayers = SAMPLE_SHAPE_BOUNDARY_LAYERS.filter((layer) => visibleBoundaryLayers.has(layer));
  const showZipBoundary = activeSampleBoundaryLayers.includes(MultiMapExportBoundaryLayer.ZipCodeBoundaries);
  const visibleContentLayers = getVisibleContentLayers(selections);
  const showBaseMapImage = usesBaseMapImage(selections);
  const isTerrain = (selections?.selectedBaseMap ?? DEFAULT_MAP_STYLE_BASE_MAP) === MultiMapExportBaseMap.Terrain;
  const sampleMapImageUrl = `${window.customUIBaseUrl}/images/multi-map-export-sample-${isTerrain ? "terrain" : "light"}.png`;
  const countyFill = getSampleCountyFill(selections);
  const showStateLabel = visibleBoundaryLayers.has(MultiMapExportBoundaryLayer.StateLabels);
  const showCountyLabel = visibleBoundaryLayers.has(MultiMapExportBoundaryLayer.CountyLabels);
  const showZipLabels = visibleBoundaryLayers.has(MultiMapExportBoundaryLayer.ZipCodeLabels);
  const showZipPoints = showZipLabels && visibleBoundaryLayers.has(MultiMapExportBoundaryLayer.ZipCodePoints);
  const isNonTerminatedVisible = visibleContentLayers.includes(ProvidersFilterOptions.NonTerminated);
  const isMapContentsZoom = (selections?.selectedZoomLevel ?? DEFAULT_MAP_STYLE_ZOOM_LEVEL) === MultiMapExportZoomLevel.MapContents;
  const activeLayers = isMapContentsZoom && isNonTerminatedVisible ? SAMPLE_PADDED_LAYERS : SAMPLE_TIGHT_LAYERS;
  const showScaleBar = selections?.showScaleBar ?? DEFAULT_MAP_STYLE_SHOW_SCALE_BAR;
  const kansasLabelPercent = projectPointToPercent(KANSAS_LABEL_POINT, activeLayers.bounds);
  const johnsonCountyLabelPercent = projectPointToPercent(JOHNSON_COUNTY_LABEL_POINT, activeLayers.bounds);

  return (
    <div className="multi-map-export__sample" data-testid="multi-map-export__sample">
      <div className="multi-map-export__sample-header">
        <h2 className="multi-map-export__sample-title">Map Export Sample</h2>
      </div>
      <div className="multi-map-export__sample-frame">
        <svg
          aria-hidden="true"
          className={cx("multi-map-export__sample-shape", {
            "multi-map-export__sample-shape--plain": !showBaseMapImage,
            "multi-map-export__sample-shape--terrain": showBaseMapImage && isTerrain,
          })}
          data-testid="multi-map-export__sample-shape"
          style={showBaseMapImage
            ? {
              backgroundImage: `url("${sampleMapImageUrl}")`,
              backgroundPosition: `${activeLayers.backgroundFit.positionX}% ${activeLayers.backgroundFit.positionY}%`,
              backgroundSize: `${activeLayers.backgroundFit.sizeX}% ${activeLayers.backgroundFit.sizeY}%`,
            }
            : undefined}
          viewBox={`0 0 ${SAMPLE_VIEW_BOX.width} ${SAMPLE_VIEW_BOX.height}`}
          preserveAspectRatio="none"
        >
          <path
            className="multi-map-export__sample-county-fill"
            d={activeLayers.countyPath}
            data-testid="multi-map-export__sample-base"
            style={countyFill ? { fill: countyFill } : undefined}
          />
          {activeShapeBoundaryLayers.map((layer) => (
            <path
              className={cx("multi-map-export__sample-boundary", `multi-map-export__sample-boundary--${layer}`)}
              d={shapePathFor(layer, activeLayers)}
              data-testid={`multi-map-export__sample-boundary-${layer}`}
              key={layer}
              style={BOUNDARY_STROKE_COLOR[layer] ? { stroke: BOUNDARY_STROKE_COLOR[layer] } : undefined}
            />
          ))}
          {showZipBoundary && (
            // One group testid, not one per path — duplicates would make getByTestId throw.
            <g data-testid="multi-map-export__sample-boundary-zipCodeBoundaries">
              {activeLayers.zipPaths.map(({ d, zipCode }) => (
                <path
                  className="multi-map-export__sample-boundary multi-map-export__sample-boundary--zipCodeBoundaries"
                  d={d}
                  key={zipCode}
                  style={{ stroke: ZIP_CODE_BORDER_COLOR }}
                />
              ))}
            </g>
          )}
        </svg>
        {/* Plain HTML, not SVG <text> — the non-uniform viewBox stretch would squash letters. */}
        {/* Same isPercentInFrame guard as the zip labels: Kansas's anchor sits right at the county's */}
        {/* northern edge, so the tight (Zoom to County) frame pushes it just past the top of the card. */}
        {showStateLabel && isPercentInFrame(kansasLabelPercent) && (
          <span className="multi-map-export__sample-place-label" style={{ left: `${kansasLabelPercent.left}%`, top: `${kansasLabelPercent.top}%` }}>Kansas</span>
        )}
        {showCountyLabel && isPercentInFrame(johnsonCountyLabelPercent) && (
          <span className="multi-map-export__sample-place-label" style={{ left: `${johnsonCountyLabelPercent.left}%`, top: `${johnsonCountyLabelPercent.top}%` }}>Johnson County</span>
        )}
        {showZipLabels && SAMPLE_ZIP_LABEL_POINTS.map((label) => renderZipLabel(label, activeLayers.bounds))}
        {showZipPoints && SAMPLE_ZIP_POINT_LABEL_POINTS.map((label) => renderZipLabel(label, activeLayers.bounds))}
        {visibleContentLayers.flatMap((layer) => {
          const option = CONTENT_LAYER_OPTIONS.get(layer);

          if (!option) return [];

          return MAP_EXPORT_SAMPLE_POINTS[layer].map((point, index) => {
            const percent = projectPointToPercent([point.lng, point.lat], activeLayers.bounds);

            return (
              <QesIcon
                className={cx("multi-map-export__sample-marker", isMemberLayer(layer)
                  ? "multi-map-export__sample-marker--member"
                  : "multi-map-export__sample-marker--provider")}
                height="0.75rem"
                iconName={option.swatchIcon}
                isDecorative={true}
                key={`${layer}-${index}`}
                pathStyle={swatchPathStyle(option.swatchColor, option.swatchIcon)}
                style={{ left: `${percent.left}%`, top: `${percent.top}%` }}
                width="0.75rem"
              />
            );
          });
        })}
        {showScaleBar && (
          <div className="multi-map-export__sample-scale-bar-card" data-testid="multi-map-export__sample-scale-bar">
            <div className="multi-map-export__sample-scale-bar">
              <span className="multi-map-export__sample-scale-bar-label multi-map-export__sample-scale-bar-label--start">0</span>
              <span className="multi-map-export__sample-scale-bar-label multi-map-export__sample-scale-bar-label--end">{activeLayers.scaleBarMiles} miles</span>
            </div>
          </div>
        )}
      </div>
      <div className="multi-map-export__sample-body">
        <p className="multi-map-export__sample-caption" data-testid="multi-map-export__sample-caption">
          Sample County - Sample Specialty
        </p>
        <span className="multi-map-export__sample-legend-heading">Map Legend</span>
        <ul className="multi-map-export__sample-legend">
          {visibleContentLayers.map((layer) => {
            const option = CONTENT_LAYER_OPTIONS.get(layer);

            return option && (
              <li key={layer}>
                <SwatchLabel color={option.swatchColor} iconName={option.swatchIcon} label={legendLabelFor(layer, option.label)} />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default MapExportSample;
